// src/components/mafia/chat/MafiaChat.js

import React, { useEffect, useRef, useState } from 'react';
import useStore from '../../../store';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaCrosshairs } from 'react-icons/fa';
import './MafiaChat.css';

const MafiaChat = ({ room, isGhost, onOpenShotMenu }) => {
  const { t } = useTranslation();
  const store = useStore.getState();
  const { telegram, work, info, mafia } = store.ref;
  const game = info.game;
  
  const chatData = room ? mafia.chats[room] : null;
  const messages = chatData ? chatData.messages : (mafia.chatHistory || []); // Fallback

  const myId = telegram.telegramUserId;
  
  const amIAlive = info.game?.mafiaPublic?.alive?.[myId];

  const [inputVal, setInputVal] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const isDrawingPhase = mafia.phase === 'draw_civilian';

  // Скролл вниз
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, room]); // Scroll on message add or chat change

  useEffect(() => {
    setTimeout(scrollToBottom, 50);
  }, []);


  // Умный ресайз поля ввода
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      if (!inputVal) {
        textareaRef.current.style.height = '44px';
      } else {
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
      }
    }
  }, [inputVal]);

  const handleSend = () => {
    const text = inputVal.trim();
    if (!text) return;

    const id = Date.now() + Math.random();
    const timestamp = Date.now();

    // Send to server with room
    work.methods.hostAction('mafiaMessage', { 
      text: text, 
      room: room,
      id: id,
      timestamp: timestamp
    });

    const tempMsg = {
      id: id,
      text: text,
      userId: myId,
      author: t('mafia.chat_sender_name', 'Me'),
      timestamp: timestamp,
      isTemp: true,
      room: room // Add room to temp message
    };
    store.ref.mafia.methods.addChatMessage(tempMsg, room);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const placeholder = isGhost
    ? t('mafia.ghost_chat_placeholder', 'Сообщение призракам...')
    : t('mafia.chat_placeholder', 'Сообщение...');

  return (
    <div
      className={`mafia-chat-container ${isGhost ? 'ghost-view' : ''}`}
    >
      <div className="mafia-chat-content">
        <div
          className="mafia-chat-messages add-scroll touch-active"
        >
          {messages.map((msg, idx) => {
            if (msg.type === 'divider') {
              return (
                <div key={msg.id || idx} className="mafia-chat-divider">
                  {msg.text}
                </div>
              );
            }

            if (msg.isSystem) {
                return (
                    <div key={msg.id || idx} className="mafia-chat-divider" style={{fontSize: '12px', color: '#9c27b0'}}>
                        {msg.text}
                    </div>
                );
            }

            // --- ЛОГИКА ГРУППИРОВКИ СООБЩЕНИЙ ---
            const isMe = String(msg.userId) === String(myId);

            // Смотрим на предыдущее сообщение
            const prevMsg = idx > 0 ? messages[idx - 1] : null;

            // Проверяем, является ли предыдущее сообщение частью той же серии
            // (тот же автор, не разделитель, не системное)
            const isChain = prevMsg &&
                            prevMsg.type !== 'divider' &&
                            !prevMsg.isSystem &&
                            String(prevMsg.userId) === String(msg.userId);

            // Показываем имя только если это не я и это начало цепочки
            const showName = !isMe && !isChain;

            return (
              <div
                key={msg.id || idx}
                className={`mafia-message-row ${isMe ? 'my-message' : 'other-message'} ${isChain ? 'chained' : ''}`}
              >
                <div className={`mafia-message-bubble ${isDrawingPhase ? 'drawing-phase' : ''}`}>
                  {showName && <div className="mafia-message-author">{msg.author}</div>}
                  <div className="mafia-message-text">{msg.text}</div>
                  <div className="mafia-message-time">
                    {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} style={{height: '1px'}} />
        </div>

        <div className="mafia-chat-input-area">
          {/* КНОПКА МИШЕНИ (только если передана функция открытия) */}
          {onOpenShotMenu && game.subMode === 'mafia' && mafia.role === 'mafia' && amIAlive && (
              <button
                className="mafia-chat-send-btn"
                style={{ marginRight: '8px', backgroundColor: '#d14e4e' }}
                onClick={onOpenShotMenu}
              >
                <FaCrosshairs />
              </button>
          )}
          <textarea
             ref={textareaRef}
             className="mafia-chat-input touch-active"
             placeholder={placeholder}
             value={inputVal}
             onChange={(e) => setInputVal(e.target.value)}
             onKeyDown={handleKeyDown}
             rows={1}
          />
          <button className="mafia-chat-send-btn" onClick={handleSend}>
            <FaPaperPlane style={{ marginLeft: '-2px' }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MafiaChat;
